import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { Notice } from '@/shared/notices/types';
import { Badge } from '@/shared/ui/Badge';
import { cn } from '@/shared/utils/cn';

import {
  internalLink,
  noticeAudienceLine,
  noticeByline,
  noticeEmailLine,
  noticeLinkLabel,
  noticeToneBadge,
} from './noticeText';

/**
 * One notice, as the window and the bell show it: the title, the text as it was written
 * (line breaks kept), who and when, where it leads, and for whoever administers how its
 * email to «Comerciales» went. A payroll notice carries the colour of its month above the
 * title. `fresh` marks one this person has not seen yet. `showAudience`, in the bell: whom
 * it goes to, which only whoever administers gets (contract v10.55).
 */
export function NoticeItem({
  notice,
  fresh = false,
  showAudience = false,
  onFollow,
  actions,
}: {
  notice: Notice;
  fresh?: boolean;
  showAudience?: boolean;
  /** The link was followed: the caller closes what it has open. */
  onFollow?: () => void;
  actions?: ReactNode;
}) {
  const link = internalLink(notice.link);
  const email = noticeEmailLine(notice);
  const tone = noticeToneBadge(notice);
  const audience = showAudience ? noticeAudienceLine(notice) : null;
  return (
    <article className="flex flex-col gap-1">
      {tone ? (
        <Badge tone={tone.tone} dot className="self-start">
          {tone.label}
        </Badge>
      ) : null}
      <h3 className="text-ink flex items-start gap-2 text-[14.5px] font-semibold">
        {fresh ? (
          <span
            aria-hidden="true"
            className="bg-accent mt-[7px] inline-block size-2 shrink-0 rounded-full"
          />
        ) : null}
        <span className="min-w-0 break-words">
          {notice.title}
          {fresh ? <span className="sr-only"> (nuevo)</span> : null}
        </span>
      </h3>
      {notice.body ? (
        <p className="text-ink-2 text-[14px] leading-relaxed break-words whitespace-pre-line">
          {notice.body}
        </p>
      ) : null}
      <p className="text-ink-3 text-[12.5px]">{noticeByline(notice)}</p>
      {audience ? <p className="text-ink-3 text-[12.5px] break-words">{audience}</p> : null}
      {email ? (
        <p className={cn('text-[12.5px]', email.failed ? 'text-danger' : 'text-ink-3')}>
          {email.text}
          {email.detail ? <span className="block">{email.detail}</span> : null}
        </p>
      ) : null}
      {link || actions ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {link ? (
            <Link to={link} onClick={onFollow} className="link text-[13.5px]">
              {noticeLinkLabel(notice)}
            </Link>
          ) : null}
          {actions}
        </div>
      ) : null}
    </article>
  );
}
